import { useEffect, useMemo, useState } from "react";
import {
  Calendar,
  dateFnsLocalizer,
  Views,
  type View,
} from "react-big-calendar";
import { format, parse, startOfWeek, getDay } from "date-fns";
import { enUS } from "date-fns/locale";
import "react-big-calendar/lib/css/react-big-calendar.css";
import {
  Spinner,
  Text,
  Title3,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { useApp } from "../context/AppContext";
import { LeaveTypeCode } from "../models/leaveModels";
import { FILEABLE_LEAVE_CODES } from "../config/leavePolicy";
import { LEAVE_TYPE_COLORS, leaveTypeColor } from "../utils/leaveTypeColors";
import { parseISODate, toISODate } from "../utils/dateUtils";

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales: { "en-US": enUS },
});

interface LeaveEvent {
  title: string;
  start: Date;
  end: Date;
  allDay: boolean;
  resource: { code?: LeaveTypeCode; name?: string };
}

const useStyles = makeStyles({
  root: { display: "flex", flexDirection: "column", gap: tokens.spacingVerticalM },
  legend: { display: "flex", flexWrap: "wrap", gap: tokens.spacingHorizontalM },
  legendItem: { display: "flex", alignItems: "center", gap: tokens.spacingHorizontalXS },
  swatch: { width: "12px", height: "12px", borderRadius: tokens.borderRadiusSmall },
  calWrap: { height: "600px" },
});

/** Month window (±1 week) for the currently focused date. */
function windowFor(date: Date): { startISO: string; endISO: string } {
  const start = new Date(date.getFullYear(), date.getMonth(), 1);
  start.setDate(start.getDate() - 7);
  const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  end.setDate(end.getDate() + 7);
  return {
    startISO: `${toISODate(start)}T00:00:00.000Z`,
    endISO: `${toISODate(end)}T23:59:59.000Z`,
  };
}

export function TeamCalendar() {
  const s = useStyles();
  const { service } = useApp();

  const [date, setDate] = useState(new Date());
  const [view, setView] = useState<View>(Views.MONTH);
  const [events, setEvents] = useState<LeaveEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const { startISO, endISO } = windowFor(date);
    service.getTeamCalendarEvents(startISO, endISO).then((raw) => {
      if (cancelled) return;
      setEvents(
        raw.map((e) => ({
          title: e.subject,
          // Parse the date portion locally so all-day markers land on the right day.
          start: parseISODate(e.start.slice(0, 10)),
          end: parseISODate(e.end.slice(0, 10)),
          allDay: true,
          resource: { code: e.leaveTypeCode, name: e.employeeName },
        })),
      );
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [service, date]);

  // The leave types people can file, plus any other type that actually appears
  // on the calendar (e.g. an older request), so every colour shown is explained.
  const legendCodes = useMemo(() => {
    const set = new Set<LeaveTypeCode>(FILEABLE_LEAVE_CODES);
    events.forEach((e) => e.resource.code && set.add(e.resource.code));
    return Array.from(set);
  }, [events]);

  return (
    <div className={s.root}>
      <Title3>Team calendar</Title3>

      <div className={s.legend}>
        {legendCodes.map((code) => (
          <span key={code} className={s.legendItem}>
            <span className={s.swatch} style={{ backgroundColor: LEAVE_TYPE_COLORS[code] }} />
            <Text size={200}>{code}</Text>
          </span>
        ))}
      </div>

      {loading && events.length === 0 ? (
        <Spinner label="Loading team calendar…" />
      ) : (
        <div className={s.calWrap}>
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            date={date}
            view={view}
            onNavigate={(d) => setDate(d)}
            onView={(v) => setView(v)}
            views={[Views.MONTH, Views.WEEK, Views.AGENDA]}
            popup
            eventPropGetter={(event: LeaveEvent) => ({
              style: {
                backgroundColor: leaveTypeColor(event.resource.code),
                border: "none",
                color: "#fff",
              },
            })}
            tooltipAccessor={(e: LeaveEvent) =>
              `${e.resource.name ?? ""} — ${e.resource.code ?? ""}`
            }
            style={{ height: "100%" }}
          />
        </div>
      )}
    </div>
  );
}
